import React from 'react';
import WhatsAppFormattedTextarea from './WhatsAppFormattedTextarea';
import WhatsAppMediaHeaderUploader from './WhatsAppMediaHeaderUploader';
import WhatsAppTemplateButtonsEditor from './WhatsAppTemplateButtonsEditor';
import { parseConsecutiveTemplateSlots, slotValidationMessage } from './whatsappTemplateSlots';
import { headerFormatAcceptAttribute, isMediaHeaderFormat } from './whatsappPreview';
import {
  META_TEMPLATE_BODY_MAX_LENGTH,
  normalizeInteraktTemplateName,
  validateTemplateBodyLength,
  type TemplateFormState,
  type TemplateHeaderFormat,
} from './whatsappTemplateForm';
import { TEMPLATE_FOOTER_MAX } from './whatsappTemplateButtons';

export const templateInputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

function VariablesHelp() {
  return (
    <p className="mt-1 text-xs text-gray-500">
      Use <code className="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">{'{{1}}'}</code>,{' '}
      <code className="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">{'{{2}}'}</code> for dynamic
      placeholders. Meta requires static text before the first variable and after the last one.
    </p>
  );
}

interface WhatsAppTemplateDraftFieldsProps {
  form: TemplateFormState;
  setForm: React.Dispatch<React.SetStateAction<TemplateFormState>>;
  nameReadOnly?: boolean;
}

export const WhatsAppTemplateDraftFields: React.FC<WhatsAppTemplateDraftFieldsProps> = ({
  form,
  setForm,
  nameReadOnly = false,
}) => {
  const headerOptions: { value: TemplateHeaderFormat; label: string }[] = [
    { value: 'NONE', label: 'None' },
    { value: 'TEXT', label: 'Text' },
    { value: 'IMAGE', label: 'Image' },
    { value: 'VIDEO', label: 'Video' },
    { value: 'DOCUMENT', label: 'Document' },
  ];

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Name &amp; language</h4>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Template code name *</label>
            <input
              className={`${templateInputClass} ${nameReadOnly ? 'cursor-not-allowed bg-gray-50 dark:bg-gray-800/60' : ''}`}
              value={form.name}
              readOnly={nameReadOnly}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onBlur={() => {
                if (nameReadOnly) return;
                const normalized = normalizeInteraktTemplateName(form.name);
                if (normalized && normalized !== form.name) {
                  setForm((prev) => ({ ...prev, name: normalized }));
                }
              }}
              placeholder="course_promo_v1"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              {nameReadOnly
                ? 'Code name cannot be changed after creation.'
                : 'Lowercase letters, numbers, and underscores only.'}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Internal label (optional)</label>
            <input
              className={templateInputClass}
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Course Promo V1"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Language</label>
            <input
              className={templateInputClass}
              value={form.languageCode}
              onChange={(e) => setForm({ ...form, languageCode: e.target.value })}
              placeholder="en"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Variable count</label>
            <input
              type="number"
              min={0}
              max={20}
              className={templateInputClass}
              value={form.variableCount}
              onChange={(e) => setForm({ ...form, variableCount: Number(e.target.value) })}
            />
            <VariablesHelp />
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Header (optional)</h4>
        <div className="mt-3 flex flex-wrap gap-2">
          {headerOptions.map(({ value, label }) => (
            <label
              key={value}
              className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium ${
                form.headerFormat === value
                  ? 'border-[#25D366] bg-[#25D366]/10 text-[#128C7E]'
                  : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              <input
                type="radio"
                name="headerFormat"
                className="sr-only"
                checked={form.headerFormat === value}
                onChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    headerFormat: value,
                    headerText: value === 'TEXT' ? prev.headerText : '',
                    headerSampleUrl: isMediaHeaderFormat(value) ? prev.headerSampleUrl : null,
                    headerSampleFileName: isMediaHeaderFormat(value) ? prev.headerSampleFileName : null,
                  }))
                }
              />
              {label}
            </label>
          ))}
        </div>
        {form.headerFormat === 'TEXT' && (
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-600">Header text *</label>
            <input
              className={templateInputClass}
              value={form.headerText}
              onChange={(e) => setForm({ ...form, headerText: e.target.value })}
              placeholder="Short header line above the body"
            />
          </div>
        )}
        {isMediaHeaderFormat(form.headerFormat) && (
          <div className="mt-3">
            <WhatsAppMediaHeaderUploader
              headerFormat={form.headerFormat}
              headerMediaUrl={form.headerSampleUrl}
              headerFileName={form.headerSampleFileName}
              onChange={({ headerMediaUrl, headerFileName }) =>
                setForm((prev) => ({
                  ...prev,
                  headerSampleUrl: headerMediaUrl,
                  headerSampleFileName: headerFileName,
                }))
              }
            />
            <p className="mt-1 text-xs text-gray-500">
              Sample {form.headerFormat.toLowerCase()} required for Meta review (
              {headerFormatAcceptAttribute(form.headerFormat)}).
            </p>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Body *</h4>
        <div className="mt-3">
          <WhatsAppFormattedTextarea
            className={templateInputClass}
            rows={5}
            value={form.bodyPreview}
            onChange={(bodyPreview) => {
              const slotInfo = parseConsecutiveTemplateSlots(bodyPreview);
              setForm({
                ...form,
                bodyPreview,
                variableCount: slotInfo.count,
              });
            }}
            placeholder={'Hi {{1}}, your appointment on {{2}} is confirmed.'}
            required
          />
          {slotValidationMessage(form.bodyPreview) && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              {slotValidationMessage(form.bodyPreview)}
            </p>
          )}
          {validateTemplateBodyLength(form.bodyPreview) && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-300">
              {validateTemplateBodyLength(form.bodyPreview)}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            {form.bodyPreview.length}/{META_TEMPLATE_BODY_MAX_LENGTH} characters
          </p>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Footer (optional)</h4>
        <div className="mt-3">
          <input
            className={templateInputClass}
            maxLength={TEMPLATE_FOOTER_MAX}
            value={form.footer}
            onChange={(e) => setForm({ ...form, footer: e.target.value })}
            placeholder="Small gray text below the message"
          />
          <p className="mt-1 text-xs text-gray-400">
            {form.footer.length}/{TEMPLATE_FOOTER_MAX} characters
          </p>
          {form.templateType === 'SIMPLE' && form.category === 'MARKETING' && (
            <label className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.includeOptOut}
                onChange={(e) =>
                  setForm({
                    ...form,
                    includeOptOut: e.target.checked,
                    footer: e.target.checked ? form.footer || 'Reply STOP to opt out' : form.footer,
                  })
                }
              />
              <span>
                <strong>Add STOP opt-out</strong> — adds footer text and a Stop quick-reply for marketing.
                With a website/deep-link button, Interakt gets the CTA only; Stop stays local + footer.
                templates.
              </span>
            </label>
          )}
        </div>
      </section>

      {form.templateType === 'WITH_BUTTONS' && (
        <WhatsAppTemplateButtonsEditor form={form} setForm={setForm} />
      )}
    </div>
  );
};

export default WhatsAppTemplateDraftFields;
